import { useTranslation } from 'react-i18next';
import type { ComplianceGroup } from '@dashboard/contracts';
import { formatNumber } from '../../i18n';

/**
 * A clean share, drawn, with the target as a notch across it.
 *
 * One component rather than two, and the reason is a bug rather than tidiness.
 * The compliance page and the dashboard tile each drew this bar, and the two
 * copies decided "is it short of target" differently: the page compared
 * `percent >= target`, the tile read `target.met`. Those disagree on exactly
 * the case the bus target exists for -- a fleet clearing 59% on biomethane
 * while missing the zero-emission half is `met: false` with a share above the
 * notch -- so the same figure was drawn green on one screen and red on the
 * other.
 *
 * The verdict is therefore not recomputed here at all. It is taken from the
 * report, which is the only place entitled to decide it (`attainmentFor` in
 * `@dashboard/contracts`), and `met: null` -- a category with no procurements
 * -- draws as neither.
 *
 * The number stays beside the bar rather than being replaced by it: the bar is
 * for comparing rows at a glance, and the figure is what somebody quotes.
 */
export function ShareBar({
  percent,
  targetPercent,
  met,
  locale,
}: {
  percent: number;
  /** Null draws no notch: a contribution row has no target by construction. */
  targetPercent: number | null;
  /** From the report. Null means there was nothing to judge. */
  met: boolean | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const clamp = (value: number) => Math.min(100, Math.max(0, value));

  return (
    <div className="share-bar">
      <div
        className="share-bar__track"
        role="img"
        aria-label={
          targetPercent === null
            ? t('fleet.shareBar.labelPlain', { share: percent })
            : t('fleet.shareBar.label', { share: percent, target: targetPercent })
        }
      >
        <div
          className={`share-bar__fill${met === false ? ' share-bar__fill--short' : ''}`}
          style={{ width: `${clamp(percent)}%` }}
        />
        {targetPercent !== null ? (
          <div className="share-bar__target" style={{ left: `${clamp(targetPercent)}%` }} />
        ) : null}
      </div>
      <span className="share-bar__value numeric">
        {formatNumber(percent, locale, { maximumFractionDigits: 1 })}%
      </span>
    </div>
  );
}

/** The bar for one report row, reading the target off the row rather than
 *  asking the caller to unpack it -- which is where the two copies drifted. */
export function GroupShareBar({ group, locale }: { group: ComplianceGroup; locale: string }) {
  return (
    <ShareBar
      percent={group.counts.cleanPercent}
      targetPercent={group.target?.minimumCleanPercent ?? null}
      met={group.target?.met ?? null}
      locale={locale}
    />
  );
}
